import { F0WizardForm } from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { PLANNING_PERIOD_STATUS } from 'modules/planning/consts/period_status'
import usePeriods from 'modules/planning/hooks/usePeriods'

import { useNewPeriodWizardFormDefinition } from './form/useNewPeriodWizardFormDefinition'
import type { WizardData } from './types'

import type { PlanningPeriodDetailQuery } from 'generated/resources/graphql'

type Period = NonNullable<PlanningPeriodDetailQuery['planning']['period']>

interface PeriodWizardProps {
  period?: Period
  onClose: () => void
  onSuccess: (periodId?: string) => void
}

export const PeriodWizard = ({ period, onClose, onSuccess }: PeriodWizardProps) => {
  const isEditMode = !!period
  const isLocked =
    period?.status === PLANNING_PERIOD_STATUS.REVIEWED ||
    period?.status === PLANNING_PERIOD_STATUS.CLOSED
  const { company } = useCurrentSession()
  const { completeCreate, completeUpdate } = usePeriods({ companyId: String(company.id) })

  const handleCreate = async (data: WizardData): Promise<boolean> => {
    const startDate = i18nDateToString(data.startDate, 'backend')
    const endDate = i18nDateToString(data.endDate, 'backend')

    const result = await completeCreate({
      name: data.name,
      startDate,
      endDate,
      periodType: 'headcount',
      plannerItems: data.scopeEmployeeIds.map((employeeId) => ({ employeeId })),
      participantItems: data.collaborators.map(({ employeeId, role }) => ({ employeeId, role })),
    })

    if (!result.success) return false

    app.setFlash({
      type: 'correct',
      message: i18n.t('planning.period_wizard.create.success_message'),
    })

    onSuccess(result.data?.period?.id)
    return true
  }

  const handleUpdate = async (data: WizardData): Promise<boolean> => {
    if (!period) return false

    const startDate = i18nDateToString(data.startDate, 'backend')
    const endDate = i18nDateToString(data.endDate, 'backend')

    const result = await completeUpdate({
      id: period.id,
      name: data.name,
      startDate,
      endDate,
      plannerItems: data.scopeEmployeeIds.map((employeeId) => ({ employeeId })),
      participantItems: data.collaborators.map(({ employeeId, role }) => ({ employeeId, role })),
    })

    if (!result.success) return false

    app.setFlash({
      type: 'correct',
      message: i18n.t('planning.period_wizard.edit.success_message'),
    })

    onSuccess(result.data?.period?.id)
    return true
  }

  const translations = {
    generalInfoStepTitle: () => i18n.t('planning.period_wizard.general_info.step_title'),
    scopeStepTitle: () => i18n.t('planning.period_wizard.scope.step_title'),
    scopeStepDescription: () => i18n.t('planning.period_wizard.scope.step_description'),
    collaboratorsStepTitle: () => i18n.t('planning.period_wizard.collaborators.step_title'),
    collaboratorsStepDescription: () =>
      i18n.t('planning.period_wizard.collaborators.step_description'),
    next: () => i18n.t('planning.period_wizard.actions.next'),
    previous: () => i18n.t('planning.period_wizard.actions.previous'),
    finish: () =>
      isEditMode
        ? i18n.t('planning.period_wizard.actions.save')
        : i18n.t('planning.period_wizard.actions.create'),
  }

  const defaultValues = period
    ? {
        general: {
          name: period.name,
          startDate: new Date(period.startDate),
          endDate: new Date(period.endDate),
        },
        scope: {
          scopeEmployeeIds: period.plannersConnection.edges
            .map((edge) => String(edge.node.employee?.id))
            .filter(Boolean),
        },
        collaborators: {
          collaborators: period.participantsConnection.edges.map((edge) => ({
            employeeId: String(edge.node.employee.id),
            role: edge.node.role,
          })),
        },
      }
    : undefined

  const { formDefinition, steps } = useNewPeriodWizardFormDefinition({
    translations,
    onFinalSubmit: isEditMode ? handleUpdate : handleCreate,
    defaultValues,
    datesDisabled: isLocked,
  })

  return (
    <F0WizardForm
      formDefinition={formDefinition}
      isOpen
      onClose={onClose}
      title={
        isEditMode
          ? i18n.t('planning.period_wizard.edit.title')
          : i18n.t('planning.period_wizard.create.title')
      }
      previousLabel={translations.previous()}
      width="xl"
      autoCloseOnLastStepSubmit
      steps={steps}
    />
  )
}
